import { BaseScalableView } from "../../../common/BaseScalableView";
import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { LatestNewsResourcePackage } from "../resources/LatestNewsResourcePackage";
import { IDestroyOptions, Rectangle, Sprite, Text, TextStyle } from "pixi.js";
import { GameFont } from "../../../common/GameFont";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { UIScrollBox } from "../../../common/components/UIScrollBox";
import { NewsItemsFactory } from "../components/NewsItemsFactory";
import { PromiseCompletionSource } from "by/gamefactory/shared/async/PromiseCompletionSource";
import { ButtonWithScaleAndPosYAnimation } from "../../../common/components/ButtonWithScaleAndPosYAnimation";
import { ILatestNewsController } from "../controllers/ILatestNewsController";
import { UserProfileResourcePackage } from "../../userprofile/resources/UserProfileResourcePackage";
import { FullscreenInputBlocker } from "../../../game/components/FullscreenInputBlocker";
import { ILatestNewsModel } from "../../../common/models/latestnews/ILatestNewsModel";
import { IPlayerModel } from "../../../common/models/IPlayerModel";
import { gsap } from "gsap";
import { IGuestModel } from "../../../common/models/IGuestModel";
import { CancellationToken } from "async/CancellationToken";
import { DynamicResourcesPackage } from "resources/DynamicResourcesPackage";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { NewsItem } from "../components/newsItems/NewsItem";
import { NewsItemPurchasing } from "../components/newsItems/NewsItemPurchasing";
import { NewsItemBuildingSold } from "../components/newsItems/NewsItemBuildingSold";
import { LatestNewsPopupSceneResult, LatestNewsResultOptions } from "../LatestNewsResultOptions";
import { WalletTabType } from "../../myWallet/components/WalletTabType";
import { Redirector } from "by/gamefactory/tools/Redirector";
import { Web3Model } from "by/gamefactory/web3/models/Web3Model";
import { Chain } from "thirdweb";
import { Web3Config } from "../../../../../web3/config/Web3Config";

export class LatestNewsPopupView extends BaseScalableView<LatestNewsResultOptions<LatestNewsPopupSceneResult>> {
    private static readonly NEWS_TEXT = 'NEWS';
    private static readonly NEWS_FROM_TEXT = 'NEWS FROM THE LAST ';
    private static readonly SCROLL_HEIGHT = 1450;
    private static readonly SCROLL_WIDTH = 800;
    private static readonly SCROLL_ELEMENTS_MARGIN = 18.5;
    private static readonly SCROLL_VERTICAL_PADDING = 45;

    private readonly cancellationToken: CancellationToken;
    private readonly commonResources: CommonResourcesPackage;
    private readonly newsDynamicResources: DynamicResourcesPackage;
    private readonly newsItemFactory: NewsItemsFactory;
    private readonly playerModel: IPlayerModel;
    private readonly guestModel: IGuestModel;
    private readonly latestNewsModel: ILatestNewsModel;
    private readonly animations: gsap.core.Timeline;
    private readonly web3Model: Web3Model;

    private viewBackground: Sprite;
    private backButton: ButtonWithScaleAndPosYAnimation;
    private newsText: Text;
    private newsFromText: Text;
    private scrollBox: UIScrollBox;
    private inputBlocker: FullscreenInputBlocker;

    public constructor(
        operationControls: PromiseCompletionSource<LatestNewsResultOptions<LatestNewsPopupSceneResult>>,
        cancellationToken: CancellationToken,
        canvasScaler: ICanvasScaler,
        newsResources: LatestNewsResourcePackage,
        commonResources: CommonResourcesPackage,
        userProfileResources: UserProfileResourcePackage,
        newsDynamicResources: DynamicResourcesPackage,
        playerModel: IPlayerModel,
        guestModel: IGuestModel,
        newsController: ILatestNewsController,
        latestNewsModel: ILatestNewsModel,
        logger: ILogger,
        web3Config: Web3Config,
        web3Model: Web3Model
    ) {
        super(operationControls, canvasScaler);
        this.cancellationToken = cancellationToken;
        this.commonResources = commonResources;
        this.newsDynamicResources = newsDynamicResources;
        this.playerModel = playerModel;
        this.guestModel = guestModel;
        this.latestNewsModel = latestNewsModel;
        this.web3Model = web3Model;
        this.newsItemFactory = new NewsItemsFactory(
            newsResources,
            userProfileResources,
            newsController,
            this.playerModel,
            this.guestModel,
            this.newsDynamicResources,
            web3Config,
            this.cancellationToken,
            logger
        );
        this.animations = new gsap.core.Timeline({ paused: true });
        this.animations.fromTo(this, { alpha: 0 }, { alpha: 1, duration: 0.2, ease: "power1.in" });
        this.build();
        this.addScrollItems();
        this.processScrollItems();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.backButton.onUp.add(this.onBackButtonClick, this);
        this.cancellationToken.onCanceled.add(this.onCancellationRequested, this);
    }

    public async start(): Promise<void> {
        await this.show();
    };

    private build(): void {
        this.viewBackground = new Sprite(this.commonResources.assets_common_blured_backgrounds_blurry_lobby_jpg);
        this.viewBackground.eventMode = "static";
        this.backButton = new ButtonWithScaleAndPosYAnimation(this.commonResources.assets_common_ui_back_png);
        this.backButton.hitArea = new Rectangle(-this.backButton.width, -this.backButton.height, this.backButton.width * 3, this.backButton.height * 3);
        this.newsText = new Text(LatestNewsPopupView.NEWS_TEXT, new NewsTextStyle());
        this.newsFromText = new Text(LatestNewsPopupView.NEWS_FROM_TEXT + this.latestNewsModel.latestNews.value.durationInHours + 'H', new NewsFromTextStyle());
        this.scrollBox = new UIScrollBox({
            width: LatestNewsPopupView.SCROLL_WIDTH,
            height: LatestNewsPopupView.SCROLL_HEIGHT,
            type: "vertical",
            elementsMargin: LatestNewsPopupView.SCROLL_ELEMENTS_MARGIN,
            vertPadding: LatestNewsPopupView.SCROLL_VERTICAL_PADDING
        });
        this.inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.inputBlocker.blocked = false;
        this.addChild(this.viewBackground, this.backButton, this.newsText, this.newsFromText, this.scrollBox);
    }

    private addScrollItems(): void {
        this.latestNewsModel.latestNews.value.items.forEach(item => {
            const newsItem = this.newsItemFactory.createItem(item);
            if (newsItem != null) {
                this.scrollBox.addItem(newsItem);
            }
        });
        if (this.latestNewsModel.latestNews.value.items.length < 6) {
            this.scrollBox.setScrollable(false);
            this.scrollBox.setWheelScrollable(false);
        }
    }

    private processScrollItems(): void {
        (this.scrollBox.items as Array<NewsItem>).forEach((newsItem) => {
            if (newsItem instanceof NewsItemPurchasing) {
                newsItem.onSeeTransactionDetails.add(this.onSeeTransactionDetailsHandle, this);
                newsItem.onShowGoldenWheel.add(this.onShowGoldenWheelHandle, this);
            }
            if (newsItem instanceof NewsItemBuildingSold) {
                newsItem.onShowMyWallet.add(this.onShowMyWalletHandle, this);
                newsItem.onSeeTransactionDetails.add(this.onSeeTransactionDetailsHandle, this);
            }
        });
    }

    private async show(): Promise<void> {
        await this.animations.play();
    }

    private async hide(): Promise<void> {
        await this.animations.reverse();
    }

    private async onBackButtonClick(): Promise<void> {
        this.inputBlocker.blocked = true;
        await this.hide();
        this.inputBlocker.blocked = false;
        this.operationControls.complete({ type: LatestNewsPopupSceneResult.DEFAULT, args: null });
    }

    private async onShowMyWalletHandle(tabType: WalletTabType): Promise<void> {
        this.inputBlocker.blocked = true;
        await this.hide();
        this.inputBlocker.blocked = false;
        this.operationControls.complete({ type: LatestNewsPopupSceneResult.MY_WALLET, args: tabType });
    }

    private async onSeeTransactionDetailsHandle(chain: Chain): Promise<void> {
        Redirector.openTransactionDetails(chain, this.web3Model.smartWalletAddress);
    }

    private async onShowGoldenWheelHandle(): Promise<void> {
        this.inputBlocker.blocked = true;
        await this.hide();
        this.inputBlocker.blocked = false;
        this.operationControls.complete({ type: LatestNewsPopupSceneResult.GOLDEN_WHEEL, args: null });
    }

    private onCancellationRequested(): void {
        this.operationControls.complete({ type: LatestNewsPopupSceneResult.DEFAULT, args: null });
        this.destroy();
    }

    private layout(_width: number, _height: number): void {
        this.backButton.position.set(55, 235);
        this.newsText.position.set(349, 162);
        this.newsFromText.position.set(271, this.newsText.position.y + this.newsText.height - 5);
        this.scrollBox.position.set(14, 325);
    }

    public destroy(options?: boolean | IDestroyOptions): void {
        this.animations?.kill();
        this.cancellationToken.onCanceled.remove(this.onCancellationRequested, this);
        this.backButton.onUp.remove(this.onBackButtonClick, this);
        (this.scrollBox.items as Array<NewsItem>).forEach((newsItem) => {
            if (newsItem instanceof NewsItemPurchasing) {
                newsItem.onSeeTransactionDetails.remove(this.onSeeTransactionDetailsHandle, this);
                newsItem.onShowGoldenWheel.remove(this.onShowGoldenWheelHandle, this);
            }
            if (newsItem instanceof NewsItemBuildingSold) {
                newsItem.onShowMyWallet.remove(this.onShowMyWalletHandle, this);
                newsItem.onSeeTransactionDetails.remove(this.onSeeTransactionDetailsHandle, this);
            }
        });
        super.destroy(options);
    }
}

class NewsTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 72,
            fill: [
                '#FCE580',
                '#F26E43'
            ],
            fillGradientType: 1,
            fillGradientStops: [0.1, 0.9]
        })
    }
}

class NewsFromTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 36,
            fill: '#FFFFFF',
            dropShadow: true,
            dropShadowAlpha: 0.5,
            dropShadowAngle: 0.7,
            dropShadowBlur: 5,
            dropShadowColor: '#0D06064D',
            dropShadowDistance: 3
        })
    }
}
